import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import useActivations from '@/src/Activations/components/hooks/useActivations';
import useActivation from '@/src/Activations/components/hooks/useActivation';
import useShare from '@/src/Share/components/hooks/useShare';
import useGetGalleries from '@/src/GalleryConfiguration/components/hooks/useGetGalleries';
import useWhitelabelSettings from '@/src/WhitelabelSettings/components/hooks/useWhitelabelSettings';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';

/*
 * Home v2 — the app landing: a conversational greeting (time + local
 * weather), Live Now rows for events capturing right now, Upcoming
 * events with venue forecasts, and a Latest Captures strip. Live
 * detection and the strip ride the org latest-media feed (newest photo
 * per activation); weather comes from Open-Meteo, no API key, and
 * degrades silently when geolocation or geocoding fail.
 */

const LIVE_WINDOW_MIN = 45;
const FEED_POLL_MS = 30 * 1000;

interface FeedItem {
    id: number;
    activationId: number;
    activationName: string;
    thumbnailUri?: string;
    thumbnailTinyUri?: string;
    createdAt: string;
    mediaCount?: number;
}

interface ActivationRow {
    id: number;
    name: string;
    date: string;
    location?: string;
    owner?: string;
    isArchived: boolean;
}

interface LiveStats {
    photos: number;
    shares: number;
    recentPhotos: number;
    galleryHref?: string;
}

const WEATHER_CODES: Record<number, string> = {
    0: 'clear',
    1: 'mostly clear',
    2: 'partly cloudy',
    3: 'overcast',
    45: 'fog',
    48: 'fog',
    51: 'drizzle',
    53: 'drizzle',
    55: 'drizzle',
    61: 'light rain',
    63: 'rain',
    65: 'heavy rain',
    66: 'freezing rain',
    67: 'freezing rain',
    71: 'light snow',
    73: 'snow',
    75: 'heavy snow',
    77: 'snow',
    80: 'showers',
    81: 'showers',
    82: 'heavy showers',
    85: 'snow showers',
    86: 'snow showers',
    95: 'thunderstorms',
    96: 'thunderstorms',
    99: 'thunderstorms',
};

function compass(deg: number) {
    const dirs = [
        'north',
        'northeast',
        'east',
        'southeast',
        'south',
        'southwest',
        'west',
        'northwest',
    ];
    return dirs[Math.round(deg / 45) % 8];
}

function greetingWord(h: number) {
    if (h < 5) return 'Working late';
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
}

function age(t: string) {
    const m = moment().diff(moment(t), 'minutes');
    if (m < 1) return 'now';
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
}

const IconExternal = () => (
    <svg
        viewBox="0 0 20 20"
        className="h-3 w-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M8 4H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-3M12 4h4v4M16 4l-7 7" />
    </svg>
);

export default function HomeV2() {
    const navigate = useNavigate();
    const authenticatedApi = useAuthenticatedApi();
    const { orgId, org } = useUserOrg();
    const { getActivations } = useActivations();
    const { getActivationSessions } = useActivation();
    const { getSharesForActivation } = useShare();
    const { getGalleriesForActivation } = useGetGalleries();
    const { getOrgDomain } = useWhitelabelSettings();

    const [now, setNow] = useState(() => moment());
    const [weather, setWeather] = useState<string | null>(null);
    const [feed, setFeed] = useState<FeedItem[]>([]);
    const [activations, setActivations] = useState<ActivationRow[]>([]);
    const [liveStats, setLiveStats] = useState<Record<number, LiveStats>>({});
    const [forecasts, setForecasts] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(true);
    const forecastCache = useRef<Record<string, string | null>>({});

    /* clock ticks so the greeting stays honest */
    useEffect(() => {
        const timer = setInterval(() => setNow(moment()), 30 * 1000);
        return () => clearInterval(timer);
    }, []);

    /* local weather for the greeting — silent on any failure */
    useEffect(() => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    const r = await fetch(
                        `https://api.open-meteo.com/v1/forecast?latitude=${pos.coords.latitude}&longitude=${pos.coords.longitude}&current=temperature_2m,relative_humidity_2m,wind_speed_10m,wind_direction_10m&temperature_unit=fahrenheit&wind_speed_unit=mph`,
                    );
                    const data = await r.json();
                    const c = data?.current;
                    if (!c) return;
                    setWeather(
                        `${Math.round(c.temperature_2m)}°F, ${Math.round(
                            c.relative_humidity_2m,
                        )}% RH, wind ${Math.round(
                            c.wind_speed_10m,
                        )} mph out of the ${compass(c.wind_direction_10m)}.`,
                    );
                } catch {
                    /* no weather line */
                }
            },
            () => {},
            { maximumAge: 30 * 60 * 1000, timeout: 8000 },
        );
    }, []);

    /* org media feed — powers Live Now + Latest Captures, polls */
    useEffect(() => {
        if (!orgId) return;
        let mounted = true;
        const load = async () => {
            try {
                const r = await authenticatedApi.get(`${orgId}/media/latest?`);
                if (mounted) setFeed(r.data ?? []);
            } catch {
                /* feed is ambient — keep the page usable without it */
            }
        };
        load();
        const timer = setInterval(load, FEED_POLL_MS);
        return () => {
            mounted = false;
            clearInterval(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    useEffect(() => {
        let mounted = true;
        getActivations()
            .then((rows: ActivationRow[]) => {
                if (mounted) setActivations(rows ?? []);
            })
            .catch(() => {})
            .finally(() => mounted && setIsLoading(false));
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const liveEvents = useMemo(
        () =>
            feed.filter(
                (f) =>
                    moment().diff(moment(f.createdAt), 'minutes') <
                    LIVE_WINDOW_MIN,
            ),
        [feed],
    );

    const liveIdsKey = liveEvents.map((f) => f.activationId).join(',');

    /* per-live-event stats (photos / shares / recent) — a couple of
       light requests for what is rarely more than 2–3 events */
    useEffect(() => {
        if (!liveIdsKey) return;
        let mounted = true;
        const ids = liveIdsKey.split(',').map(Number);
        const load = async () => {
            const base = await getOrgDomain(org?.id).catch(() => '');
            const entries = await Promise.all(
                ids.map(async (id) => {
                    try {
                        const [sessions, shares, galleries] =
                            await Promise.all([
                                getActivationSessions(id),
                                getSharesForActivation(id),
                                getGalleriesForActivation(id),
                            ]);
                        const seenS = new Set();
                        const uniq = (sessions ?? []).filter((s: any) => {
                            const k = s.photoSessionId ?? s.id;
                            if (seenS.has(k)) return false;
                            seenS.add(k);
                            return true;
                        });
                        const seenSh = new Set();
                        const uniqShares = (shares ?? []).filter((s: any) => {
                            if (seenSh.has(s.id)) return false;
                            seenSh.add(s.id);
                            return true;
                        });
                        const photos = uniq.reduce(
                            (sum: number, s: any) =>
                                sum + (Number(s.total) || 0),
                            0,
                        );
                        const recentPhotos = uniq
                            .filter(
                                (s: any) =>
                                    moment().diff(
                                        moment(s.createdAt ?? s.date),
                                        'minutes',
                                    ) < 30,
                            )
                            .reduce(
                                (sum: number, s: any) =>
                                    sum + (Number(s.total) || 0),
                                0,
                            );
                        const slug = galleries?.[0]?.slug;
                        return [
                            id,
                            {
                                photos,
                                shares: uniqShares.length,
                                recentPhotos,
                                galleryHref:
                                    slug && base
                                        ? `${base}/gallery/${slug}`
                                        : undefined,
                            },
                        ] as const;
                    } catch {
                        return [
                            id,
                            { photos: 0, shares: 0, recentPhotos: 0 },
                        ] as const;
                    }
                }),
            );
            if (mounted) setLiveStats(Object.fromEntries(entries));
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveIdsKey, org?.id]);

    const upcoming = useMemo(() => {
        const liveIds = new Set(liveEvents.map((f) => f.activationId));
        return activations
            .filter(
                (a) =>
                    !a.isArchived &&
                    !liveIds.has(a.id) &&
                    moment(a.date).isSameOrAfter(moment().startOf('day')),
            )
            .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())
            .slice(0, 5);
    }, [activations, liveEvents]);

    /* venue forecast per upcoming event — geocode the location string
       (Open-Meteo, 16-day range), cache per location+date, fail silent */
    useEffect(() => {
        let mounted = true;
        const load = async () => {
            for (const a of upcoming) {
                const date = moment(a.date);
                if (
                    !a.location ||
                    date.diff(moment(), 'days') > 15 ||
                    !date.isValid()
                )
                    continue;
                const key = `${a.location}|${date.format('YYYY-MM-DD')}`;
                if (key in forecastCache.current) continue;
                forecastCache.current[key] = null;
                try {
                    const parts = a.location.split(',').map((p) => p.trim());
                    const candidates = [parts[parts.length - 1], parts[0]];
                    let geo: any = null;
                    for (const q of candidates) {
                        if (!q) continue;
                        const r = await fetch(
                            `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(
                                q,
                            )}&count=1`,
                        );
                        const data = await r.json();
                        if (data?.results?.[0]) {
                            geo = data.results[0];
                            break;
                        }
                    }
                    if (!geo) continue;
                    const day = date.format('YYYY-MM-DD');
                    const r = await fetch(
                        `https://api.open-meteo.com/v1/forecast?latitude=${geo.latitude}&longitude=${geo.longitude}&daily=temperature_2m_max,weather_code&temperature_unit=fahrenheit&timezone=auto&start_date=${day}&end_date=${day}`,
                    );
                    const data = await r.json();
                    const t = data?.daily?.temperature_2m_max?.[0];
                    const code = data?.daily?.weather_code?.[0];
                    if (t == null) continue;
                    const label = `${Math.round(t)}°F${
                        WEATHER_CODES[code] ? `, ${WEATHER_CODES[code]}` : ''
                    }`;
                    forecastCache.current[key] = label;
                    if (mounted)
                        setForecasts((f) => ({ ...f, [key]: label }));
                } catch {
                    /* no forecast for this row */
                }
            }
        };
        load();
        return () => {
            mounted = false;
        };
    }, [upcoming]);

    const captures = useMemo(
        () =>
            [...feed]
                .sort(
                    (a, b) =>
                        moment(b.createdAt).valueOf() -
                        moment(a.createdAt).valueOf(),
                )
                .filter((f) => f.thumbnailTinyUri || f.thumbnailUri)
                .slice(0, 12),
        [feed],
    );

    const greeting = `${greetingWord(now.hour())} — it's ${now.format(
        'h:mm A',
    )} on ${now.format('dddd, MMM D')}.`;

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[880px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-16 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    return (
        <main className="mx-auto max-w-[880px] px-[clamp(18px,3vw,34px)] pt-4">
            {/* greeting */}
            <div className="mb-7 text-[13px] text-muted-foreground">
                {greeting}
                {weather && <> {weather}</>}
            </div>

            {/* live now */}
            {liveEvents.length > 0 && (
                <section className="mb-9">
                    <div className="ps-label mb-1.5">Live now</div>
                    {liveEvents.map((f) => {
                        const stats = liveStats[f.activationId];
                        return (
                            <div
                                key={f.activationId}
                                onClick={() =>
                                    navigate(
                                        `/v2/activations/${f.activationId}`,
                                    )
                                }
                                className="mt-2.5 flex cursor-pointer items-center gap-3.5 rounded-[14px] border border-border px-4 py-[15px] transition-colors hover:border-faint"
                            >
                                <span className="ps-pulse" />
                                <div className="min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <span className="truncate text-[14px] font-semibold tracking-[-0.01em]">
                                            {f.activationName}
                                        </span>
                                        {stats?.galleryHref && (
                                            <a
                                                href={stats.galleryHref}
                                                target="_blank"
                                                rel="noreferrer"
                                                title="Open gallery"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                className="flex h-[22px] w-[22px] items-center justify-center rounded-[6px] text-faint hover:bg-secondary hover:text-foreground"
                                            >
                                                <IconExternal />
                                            </a>
                                        )}
                                    </div>
                                    <div className="mt-0.5 text-[12px] text-faint">
                                        {stats
                                            ? `${stats.recentPhotos} photo${
                                                  stats.recentPhotos === 1
                                                      ? ''
                                                      : 's'
                                              } in the last 30 min · last capture ${age(
                                                  f.createdAt,
                                              )} ago`
                                            : `last capture ${age(
                                                  f.createdAt,
                                              )} ago`}
                                    </div>
                                </div>
                                <div className="ml-auto flex items-center gap-[22px]">
                                    {stats && (
                                        <>
                                            <div className="text-right">
                                                <div className="text-[15px] font-semibold tabular-nums">
                                                    {stats.photos.toLocaleString()}
                                                </div>
                                                <div className="mt-px font-mono text-[9px] uppercase tracking-[0.1em] text-faint">
                                                    Photos
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-[15px] font-semibold tabular-nums">
                                                    {stats.shares.toLocaleString()}
                                                </div>
                                                <div className="mt-px font-mono text-[9px] uppercase tracking-[0.1em] text-faint">
                                                    Shares
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    <span className="text-[14px] text-faint">
                                        →
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </section>
            )}

            {/* upcoming */}
            <section className="mb-9">
                <div className="mb-1 flex items-center justify-between">
                    <span className="ps-label">Upcoming</span>
                    <button
                        type="button"
                        onClick={() => navigate('/v2/activations')}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                    >
                        All activations →
                    </button>
                </div>
                {upcoming.length === 0 && (
                    <p className="py-3 text-[13px] text-muted-foreground">
                        Nothing scheduled.
                    </p>
                )}
                {upcoming.map((a, i) => {
                    const d = moment(a.date);
                    const fx =
                        forecasts[
                            `${a.location}|${d.format('YYYY-MM-DD')}`
                        ];
                    return (
                        <div
                            key={a.id}
                            onClick={() => navigate(`/v2/activations/${a.id}`)}
                            className={
                                'group flex cursor-pointer items-center gap-[13px] py-3 ' +
                                (i > 0 ? 'border-t border-line' : '')
                            }
                        >
                            <div className="w-[52px] flex-none rounded-[9px] border border-border py-[5px] text-center">
                                <div className="font-mono text-[9px] uppercase tracking-[0.08em] text-attention">
                                    {d.format('MMM')}
                                </div>
                                <div className="text-[16px] font-semibold leading-tight tabular-nums">
                                    {d.format('DD')}
                                </div>
                            </div>
                            <div className="min-w-0">
                                <div className="truncate text-[13.5px] font-semibold group-hover:text-brand-ink">
                                    {a.name}
                                </div>
                                <div className="mt-px truncate text-[12px] text-faint">
                                    {[
                                        a.location,
                                        a.owner ? `Lead: ${a.owner}` : null,
                                    ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                    {fx && (
                                        <>
                                            {(a.location || a.owner) && ' · '}
                                            <span className="text-muted-foreground">
                                                {fx}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="ml-auto whitespace-nowrap text-right text-[12px] text-faint">
                                {d.isSame(moment(), 'day')
                                    ? 'today'
                                    : d.fromNow()}
                            </div>
                        </div>
                    );
                })}
            </section>

            {/* latest captures */}
            {captures.length > 0 && (
                <section className="mb-9">
                    <div className="ps-label mb-1.5">Latest captures</div>
                    <div
                        className="flex gap-2.5 overflow-x-auto pb-1 pt-2.5"
                        style={{ scrollbarWidth: 'none' }}
                    >
                        {captures.map((c) => (
                            <div
                                key={c.id}
                                onClick={() =>
                                    navigate(
                                        `/v2/activations/${c.activationId}`,
                                    )
                                }
                                className="w-[84px] flex-none cursor-pointer"
                            >
                                <img
                                    src={c.thumbnailTinyUri ?? c.thumbnailUri}
                                    alt={c.activationName}
                                    loading="lazy"
                                    className="h-[84px] w-[84px] rounded-[10px] object-cover"
                                />
                                <div className="mt-[5px] overflow-hidden text-ellipsis whitespace-nowrap text-[10.5px] text-faint">
                                    <b className="font-semibold text-muted-foreground">
                                        {c.activationName}
                                    </b>{' '}
                                    · {age(c.createdAt)}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </main>
    );
}
